/** Read-only history dialog for the satisfaction column. */

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/shared/components/ui/dialog";
import { cn } from "@/shared/lib/utils";
import {
	SATISFACTION_HINTS,
	satisfactionClass,
	useSatisfactionHistory,
	type SatisfactionEntry,
	type SatisfactionScore,
} from "../useSatisfaction";

function formatDateTime(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	return d.toLocaleString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

interface HistoryDialogProps {
	profId: string | null;
	profName: string;
	onClose: () => void;
}

export function SatisfactionHistoryDialog({
	profId,
	profName,
	onClose,
}: HistoryDialogProps) {
	// The API already returns newest-first; no client-side sort to drift from it.
	const { data, isLoading, error } = useSatisfactionHistory(profId);

	return (
		<Dialog open={!!profId} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle className="text-base">
						Satisfaction history — {profName}
					</DialogTitle>
				</DialogHeader>

				{isLoading && (
					<p className="py-6 text-center text-sm text-muted-foreground">
						Loading…
					</p>
				)}
				{error && (
					<p className="py-6 text-center text-sm text-destructive">
						Could not load history.
					</p>
				)}
				{!isLoading && !error && (data?.length ?? 0) === 0 && (
					<p className="py-6 text-center text-sm text-muted-foreground">
						No scores recorded yet.
					</p>
				)}

				{!isLoading && !error && (data?.length ?? 0) > 0 && (
					<ul className="max-h-[50vh] space-y-2 overflow-y-auto">
						{data?.map((entry: SatisfactionEntry) => (
							<li
								key={entry.id}
								className="flex items-start gap-3 rounded border p-2.5"
							>
								<span
									className={cn(
										"mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-xs font-medium",
										satisfactionClass(entry.score),
									)}
								>
									{entry.score ?? "Note only"}
								</span>
								<div className="min-w-0 flex-1">
									<div className="text-xs text-muted-foreground">
										{entry.score === null
											? "No rating on this entry"
											: // Falls back to the raw value, never to an empty
												// line: a score this build cannot gloss is still
												// something a reader needs to see.
												(SATISFACTION_HINTS[
													entry.score as SatisfactionScore
												] ?? entry.score)}
									</div>
									<div className="text-[11px] text-muted-foreground">
										{formatDateTime(entry.createdAt)}
									</div>
									{entry.note && (
										<p className="mt-1 whitespace-pre-wrap text-xs">
											{entry.note}
										</p>
									)}
								</div>
							</li>
						))}
					</ul>
				)}
			</DialogContent>
		</Dialog>
	);
}
